/*
 * Copyright (c) 2024 Anvilor Solutions LLC - All Rights Reserved.
 *
 * The information and source code contained herein is the exclusive property of
 * Anvilor Solutions LLC and may not be disclosed, examined, or reproduced in whole
 * or in part without the explicit written authorization from Anvilor Solutions LLC.
 */

import { Injectable } from "@angular/core";
import { InjectionToken } from "@angular/core";
import { Optional } from "@angular/core";
import { Inject } from "@angular/core";
import { lastValueFrom } from "rxjs";
import { pass } from "@anvilor/ngx-core-utils";
import { ThemeStoreService } from "@anvilor/ngx-theming";
import { LIGHT } from "@anvilor/ngx-theming";
import { take } from "rxjs/operators";
import { JsApiScriptLoader } from "@anvilor/ngx-core-utils";

declare const grecaptcha: any;

export const GRECAPTCHA_SITE_KEY = new InjectionToken<string>("GRECAPTCHA_SITE_KEY");

const GRECAPTCHA_JS_API = "https://www.google.com/recaptcha/api.js";

const _WINDOW_CAPTCHA_SOLVED = "anvilor_grecaptchaCallback";
const _WINDOW_CAPTCHA_EXPIRED = "anvilor_grecaptchaExpired";
const _WINDOW_CAPTCHA_ERROR = "anvilor_grecaptchaError";

/**
 * Simple facade to the actual loader above.
 */
@Injectable({
  providedIn: "root",
})
export class RecaptchaService {
  private apiLoader: JsApiScriptLoader;

  constructor(
    private themeStore: ThemeStoreService,
    @Optional() @Inject(GRECAPTCHA_SITE_KEY) private siteKey?: string
  ) {
    this.apiLoader = new JsApiScriptLoader({
      name: "grecaptcha",
      scriptSrc: (cb: string) => `${GRECAPTCHA_JS_API}?onload=${cb}&render=explicit`,
      loadAsync: true,
      defer: true,
    });
  }

  async renderChallengeWidget(
    el: Element,
    options?: {
      onSolved?: (response: string) => any;
      onExpired?: () => any;
      onError?: () => any;
    }
  ): Promise<void> {
    await this.apiLoader.ensureApiLoaded();
    let themeContrast$ = this.themeStore.themeContrast$.pipe(take(1));
    let themeContrast = await lastValueFrom(themeContrast$);

    // @ts-ignore
    window[_WINDOW_CAPTCHA_SOLVED] = options?.onSolved ?? ((_) => {});
    // @ts-ignore
    window[_WINDOW_CAPTCHA_EXPIRED] = options?.onExpired ?? pass;
    // @ts-ignore
    window[_WINDOW_CAPTCHA_ERROR] = options?.onError ?? pass;

    // noinspection TypeScriptValidateJSTypes
    grecaptcha.render(el, {
      sitekey: this.siteKey,
      theme: themeContrast === LIGHT ? "dark" : "light",
      callback: _WINDOW_CAPTCHA_SOLVED,
      "expired-callback": _WINDOW_CAPTCHA_EXPIRED,
      "error-callback": _WINDOW_CAPTCHA_ERROR,
    });
  }
}
